/**
 * "Start All FC/F2F" — aanzetten met een venster dat je nog kunt aanpassen.
 *
 * 16-09 (Jordy): de knop vult de datums zelf in — Start at is nu + 24 uur (de
 * aanloop die FanCentro nodig heeft) en Post until het einde van de maand —
 * maar soms moet het anders: "voor de 19e hebben we de rate limit bereikt en
 * ik wil nu 20-09 00:00 gaan beginnen." Daarom eerst dit venster, met de
 * datums ingevuld en aanpasbaar.
 *
 * Laat je ze staan, dan geldt per model ook de vervolgregel: een model dat al
 * vooruit gepland is begint niet opnieuw over 24 uur maar gaat verder na zijn
 * laatste geplande post. Kies je zelf een datum, dan geldt die precies zoals
 * hij er staat, voor elk model dat aangaat.
 */
import { useEffect, useState } from 'react'
import { api } from '../lib/api'

interface Proef {
  models: number
  f2f_rule?: string | null
  model_names: string[]
  posts_te_gaan: number
  start_at?: string
  post_until?: string
  continued?: { name: string; start_at: string }[]
  per_model?: { name: string; start_at: string; post_until: string }[]
  changed?: number
}

export function StartAllDialog({
  platform,
  onClose,
  onDone,
}: {
  platform: 'fancentro' | 'f2f'
  onClose: () => void
  onDone: (msg: string) => void
}) {
  const naam = platform === 'f2f' ? 'F2F' : 'FanCentro'
  /** 07-10 (Jordy): F2F kent geen 24 uur aanloop. Elk model gaat verder 30
   *  minuten na zijn eigen laatste post (of nu); alleen Post until kies je. */
  const isF2f = platform === 'f2f'
  const [proef, setProef] = useState<Proef | null>(null)
  const [start, setStart] = useState('')
  const [eind, setEind] = useState('')
  const [fout, setFout] = useState('')
  const [bezig, setBezig] = useState(false)
  /** 16-09 (Jordy): per model een dag na zijn eigen laatste post verder, zodat
   *  modellen die verschillend ver staan niet allemaal op dezelfde datum
   *  beginnen. */
  const [naLaatste, setNaLaatste] = useState(false)

  useEffect(() => {
    let weg = false
    const vraag = `/api/admin/posting/knoppen/start?dry_run=true&platform=${platform}`
      + (naLaatste ? '&na_laatste_post_uren=24' : '')
    api.post<Proef>(vraag)
      .then((p) => {
        if (weg) return
        setProef(p)
        if (!naLaatste) {
          setStart(String(p?.start_at ?? ''))
          setEind(String(p?.post_until ?? ''))
        }
      })
      .catch((e) => { if (!weg) setFout(e instanceof Error ? e.message : String(e)) })
    return () => { weg = true }
  }, [platform, naLaatste])

  const eigenDatum = !!proef && (start !== (proef.start_at ?? '') || eind !== (proef.post_until ?? ''))
  const korteAanloop = !isF2f && !!start && new Date(start).getTime() - Date.now() < 24 * 3600 * 1000

  async function starten() {
    setFout('')
    if (isF2f) {
      if (!eind) { setFout('Fill in Post until.'); return }
    } else if (!naLaatste) {
      if (!start || !eind) { setFout('Fill in both dates.'); return }
      if (new Date(eind) <= new Date(start)) { setFout('Post until must be after Planning starts at.'); return }
    }
    setBezig(true)
    try {
      const uit = await api.post<Proef>(
        `/api/admin/posting/knoppen/start?dry_run=false&platform=${platform}`
        + (isF2f
          ? `&post_until=${encodeURIComponent(eind)}`
          : naLaatste
            ? '&na_laatste_post_uren=24'
            : `&start_at=${encodeURIComponent(start)}&post_until=${encodeURIComponent(eind)}`))
      onDone(`Started on ${naam}: ${uit?.changed ?? 0} model(s) on`
        + (isF2f ? ', each 30 min after its own last post (or now)'
          : naLaatste ? ', each 24 h after its own last post' : `, from ${start.replace('T', ' ')}`))
      onClose()
    } catch (e) {
      setFout(e instanceof Error ? e.message : String(e))
    } finally {
      setBezig(false)
    }
  }

  return (
    <div
      // Niet sluiten bij een klik op de achtergrond; zie Modal.tsx (27-09). Sluiten via Cancel.
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)', zIndex: 99998,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div className="panel" style={{ width: 520, maxWidth: 'calc(94vw / var(--dz, 1))', margin: 0 }}>
        <div className="head">
          <div>
            <h3>{`Start All ${naam}`}</h3>
            <p>
              {proef
                ? (isF2f
                  ? `${proef.models} model(s) to start (off, or on with nothing planned)`
                  : `${proef.models} model(s) off right now · ${proef.posts_te_gaan} posts waiting in their schedule`)
                : 'Loading…'}
            </p>
          </div>
        </div>
        <div className="body">
          {isF2f ? null : (
            <>
              <label className="chkrow" style={{ marginBottom: 10 }}>
                <span className={naLaatste ? 'chk on' : 'chk'} onClick={() => setNaLaatste((v) => !v)}>
                  {naLaatste ? '✓' : ''}
                </span>
                Start each model 24 hours after its own last post
              </label>
              <label className="mut2" style={{ fontSize: 11 }}>Planning starts at</label>
              <input className="inp" type="datetime-local" style={{ width: '100%' }}
                     value={start} onChange={(e) => setStart(e.target.value)}
                     disabled={naLaatste} />
            </>
          )}
          <label className="mut2" style={{ fontSize: 11, marginTop: 8, display: 'block' }}>
            Post until
          </label>
          <input className="inp" type="datetime-local" style={{ width: '100%' }}
                 value={eind} onChange={(e) => setEind(e.target.value)}
                 disabled={naLaatste} />

          <div className="mut2" style={{ fontSize: 11, marginTop: 8, lineHeight: 1.5 }}>
            {isF2f
              ? 'Each model continues 30 minutes after its own last post on F2F, or right away if '
                + 'that was longer ago. At most 10 posts per 24 hours per model, spread by its '
                + 'interval. No 24-hour lead time: that rule is only for FanCentro.'
              : naLaatste
              ? 'Every model starts 24 hours after its own last planned post, so models that are '
                + 'further along keep their lead. Post until becomes the end of that month, and '
                + 'never within the 24 hours FanCentro needs.'
              : eigenDatum
                ? 'Your own date is used exactly as it is, for every model that starts.'
                : 'Prefilled: 24 hours from now (FanCentro\'s lead time) until the end of the '
                  + 'month. A model that is already planned ahead continues after its last '
                  + 'planned post.'}
            {` Only ${naam}; the other platform and running models are left alone.`}
          </div>
          {korteAanloop ? (
            <div style={{ color: 'var(--warn)', fontSize: 12, marginTop: 8 }}>
              Less than 24 hours from now — FanCentro usually will not publish those posts.
            </div>
          ) : null}
          {(naLaatste || isF2f) && proef?.per_model?.length ? (
            <div className="mut2" style={{ fontSize: 11, marginTop: 8, maxHeight: 150, overflow: 'auto' }}>
              {proef.per_model.map((m) => (
                <div key={m.name}>{`${m.name}: ${m.start_at.replace('T', ' ')} → ${m.post_until.replace('T', ' ')}`}</div>
              ))}
            </div>
          ) : null}
          {!isF2f && !naLaatste && !eigenDatum && proef?.continued?.length ? (
            <div className="mut2" style={{ fontSize: 11, marginTop: 8 }}>
              Continues after their last planned post:{' '}
              {proef.continued.map((c) => `${c.name} (${c.start_at.replace('T', ' ')})`).join(', ')}
            </div>
          ) : null}
          {fout ? <div style={{ color: 'var(--bad)', fontSize: 12, marginTop: 8 }}>{fout}</div> : null}

          <div className="row" style={{ gap: 8, marginTop: 12 }}>
            <button className="btn primary sm" type="button" disabled={bezig || !proef?.models}
                    onClick={() => starten()}>
              {bezig ? 'Starting…' : `Start ${proef?.models ?? 0} model(s)`}
            </button>
            <button className="btn ghost sm" type="button" disabled={bezig}
                    onClick={onClose}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  )
}
